import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService } from 'src/app/auth/auth.service';
import { Role } from '../../../../shared/models/auth/role';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { FleetInstallerService } from '../../services/fleet-installer.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ToastPanelType } from 'src/app/config/app-constants';
import { MatStepper } from '@angular/material/stepper';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { LaunchDarklyService } from 'src/app/shared/services/launch-darkly.service';
import { Subscription } from 'rxjs';
import { SitePrepService } from '../../services/site-prep.service';


@Component({
  selector: 'onboard-vm-dialog',
  templateUrl: './onboard-vm-dialog.html',
  styleUrls: ['./onboard-vm-dialog.scss'],
  standalone: false,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class OnboardVmDialogComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;

  canShowPhysicalDevice: boolean = false;
  sitePreparationEnabled: boolean = false;

  currentStepIndex: number = 0;

  // Step 1 — Site Readiness
  sitePreparationCmd: string = '';
  sitePreparationRequestInProgress: boolean = false;

  // Step 2 — Install Stratus
  onboardVmForm: FormGroup;
  cmd: string = '';
  hubDeviceId: string = '';
  requestInProgress: boolean = false;

  private readonly deviceIdPattern: RegExp = /^[A-Za-z0-9:._\- ]*[A-Za-z0-9-]$/;
  private readonly maxCombinedLength: number = 60;

  private flagSubscription: Subscription | undefined;

  constructor(
    private readonly fb: FormBuilder,
    private readonly fleetInstallerService: FleetInstallerService,
    private readonly sitePrepService: SitePrepService,
    private readonly toastService: ToastService,
    private readonly authService: AuthService,
    private readonly ldService: LaunchDarklyService
  ) {
    this.onboardVmForm = this.fb.group({
      organizationId: ['', Validators.required],
      deviceId: ['', [Validators.required, Validators.pattern(this.deviceIdPattern)]],
      physicalDevice: [false]
    });
  }

  get organizationId(): string { return this.onboardVmForm.get('organizationId')?.value ?? ''; }
  get deviceId(): string { return this.onboardVmForm.get('deviceId')?.value ?? ''; }
  get physicalDevice(): boolean { return this.onboardVmForm.get('physicalDevice')?.value ?? false; }

  isCombinedLengthExceeded(): boolean {
    const organizationId: string = this.onboardVmForm?.get('organizationId')?.value ?? '';
    const deviceId: string = this.onboardVmForm?.get('deviceId')?.value ?? '';
    return (deviceId + organizationId).length > this.maxCombinedLength;
  }

  ngOnInit(): void {
    this.authService.profile$.subscribe({
      next: (profile) => {
        this.canShowPhysicalDevice = profile.hasRole(Role.parataDev);
      },
      error: (err) => {
        this.canShowPhysicalDevice = false;
      }
    });

    this.flagSubscription = this.ldService.flagChange.subscribe((flags) => {
      this.sitePreparationEnabled = flags['release-site-preparation-check'] ?? false;
      if (this.sitePreparationEnabled) {
        this.getSitePreparationCommand();
      }
    });
  }

  ngOnDestroy() {
    if (this.flagSubscription) {
      this.flagSubscription.unsubscribe();
    }
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.currentStepIndex = event.selectedIndex;
  }

  nextStep(): void {
    this.stepper?.next();
  }

  prevStep(): void {
    this.stepper?.previous();
  }

  getSitePreparationCommand(): void {
    this.sitePreparationCmd = '';
    this.sitePreparationRequestInProgress = true;
    this.sitePrepService.createSitePreparationScript().subscribe({
      next: response => {
        this.sitePreparationRequestInProgress = false;
        const url = `${environment.apiUrl.deviceMgmtService}s/${response.shortUrlCode}`;
        this.sitePreparationCmd = `curl -fsSL ${url} | sudo bash -s`;
      },
      error: e => {
        this.sitePreparationRequestInProgress = false;
        console.error(e);
        this.toastService.openToast('Unexpected error generating site readiness script', ToastPanelType.error);
      }
    });
  }

  getCommand(): void {
    this.cmd = '';
    this.hubDeviceId = '';
    this.requestInProgress = true;
    this.fleetInstallerService.createDeviceInstaller(this.organizationId, this.deviceId, this.physicalDevice).subscribe({
      next: response => {
        this.requestInProgress = false;
        const url = `${environment.apiUrl.deviceMgmtService}i/${response.shortUrlCode}`;
        if (this.physicalDevice) {
          this.cmd = `curl -fsSL ${url} | sudo bash -s -- -d`;
        } else {
          this.cmd = `curl -fsSL ${url} | sudo bash -s`
          this.hubDeviceId = response.hubDeviceId;
        }
      },
      error: e => {
        this.requestInProgress = false;
        console.error(e);

        if (e.status === 400) {
          this.toastService.openToast(e.error, ToastPanelType.error);
          return;
        }

        this.toastService.openToast('Unexpected error generating Stratus web installer', ToastPanelType.error);
      }
    });
  }
}
